import { SHM_VARIABLES } from '../interfaces/shm.interface';

/**
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ MONITORING DATA EXPLORER — TYPE DEFINITIONS                              │
 * └──────────────────────────────────────────────────────────────────────────┘
 * This catalog is the definition of what the Monitoring Data Explorer can plot, per
 * measurement type: its variables (with HOW and WHERE each is stored, including its own
 * time vector) and its dimensions (channel, electrode…). The explorer form (front end)
 * and the aggregation pipeline (back end) are both derived from it.
 *
 * It is NOT a general measurement registry. Other per-type behaviour lives elsewhere:
 *   - Measurement summary → SUMMARY_TYPE_REGISTRY (libs/angular/ui … /summarize-measurements/registry)
 *   - Compare             → COMPARE_TYPE_REGISTRY   (libs/angular/ui … /compare-measurements/registry)
 *   - Display config      → MEASUREMENT_TYPE_DISPLAY_CONFIG (libs/shared … measurement-display.model.ts)
 *
 * Adding a type = adding an entry here (reusing an existing source kind) — no changes to
 * the explorer form or the pipeline are needed. A new storage layout = a new source kind.
 */

/** Name of the stream field produced by `channelVector` sources (SHM channel key, e.g. "channel1"). */
export const CHANNEL_DIMENSION = 'channel';

/** Measurement-level fields shared by every type: groupable and categorical-filterable. */
export const EXPLORER_MEASUREMENT_FIELDS = ['name', 'device.name', 'active'] as const;

export type ExplorerSource =
  /** Maturity / Temperature: parallel arrays at the document root (`time[]`, `<field>[]`). */
  | { kind: 'rootVector'; field: string; timeField: string }
  /**
   * SHM: `channelData.<channelKey>.<field>[]` with the variable's own time vector
   * (`timeField`, falling back to `timeFallback` for older documents). Only channels whose
   * `dataChannels.<key>.type` is in `channelTypes` contribute. Emits the `channel` dimension.
   */
  | { kind: 'channelVector'; field: string; timeField: string; timeFallback?: string; channelTypes: string[] }
  /** Safecor (CONTRACT ONLY — not implemented): array of reading objects, e.g. `data.electrode{n}.rangeData[]`. */
  | { kind: 'readingArray'; arrayPath: string; field: string; timeField: string; dimension: string };

export interface ExplorerVariable {
  key: string;
  labelKey: string;
  unit: string;
  /** Sub-group in the variable picker: 'common' | 'corrosion' | 'temperature' … */
  section: string;
  source: ExplorerSource;
}

/** A dimension is offered as a group-by option AND a categorical filter, never preselected. */
export interface ExplorerDimension {
  key: string;
  labelKey: string;
}

export interface ExplorerTypeDefinition {
  measurementType: string;
  variables: ExplorerVariable[];
  dimensions: ExplorerDimension[];
}

const root = (key: string, unit: string): ExplorerVariable => ({
  key, labelKey: `variables.${key}`, unit, section: 'common',
  source: { kind: 'rootVector', field: key, timeField: 'time' },
});

const corrosionDefs = SHM_VARIABLES.corrosion as Record<string, { label: string; unit: string }>;

const shmCorrosionVariables: ExplorerVariable[] = Object.keys(corrosionDefs)
  .filter((key) => key !== 'charge') // computed + stored, but not a meaningful user-facing curve
  .map((key) => ({
    key,
    labelKey: `measurement.shm.variables.${key}`,
    unit: corrosionDefs[key].unit,
    section: 'corrosion',
    source: {
      kind: 'channelVector' as const,
      field: key,
      timeField: key === 'ecorr' ? 'time_ecorr' : 'time',
      ...(key === 'ecorr' ? { timeFallback: 'time' } : {}),
      channelTypes: ['corrosion'],
    },
  }));

const shmTemperatureVariables: ExplorerVariable[] = ['tempNtc1', 'tempNtc2', 'tempPcb'].map((key) => ({
  key,
  labelKey: `measurement.shm.variables.${key}`,
  unit: 'ºC',
  section: 'temperature',
  source: { kind: 'channelVector' as const, field: key, timeField: 'time', channelTypes: ['corrosion'] },
}));

export const MONITORING_EXPLORER_TYPES: Record<string, ExplorerTypeDefinition> = {
  MaturityMeasurement: {
    measurementType: 'MaturityMeasurement',
    variables: [root('strength', 'MPa'), root('maturity', 'ºC·day'), root('temperature', 'ºC')],
    dimensions: [],
  },
  TemperatureMeasurement: {
    measurementType: 'TemperatureMeasurement',
    variables: [root('temperature', 'ºC')],
    dimensions: [],
  },
  StructuralHealthMeasurement: {
    measurementType: 'StructuralHealthMeasurement',
    variables: [...shmCorrosionVariables, ...shmTemperatureVariables],
    dimensions: [{ key: CHANNEL_DIMENSION, labelKey: 'monitoring.labels.channel' }],
  },
  // SafecorMeasurement (CorrosionMeasurement): not defined yet — contract only (see `readingArray`).
};
